import { stringify } from "query-string";
import http from "./httpService";
import { getMonitoringJwt } from "./authService";
import IncidentData from "../data/incidents.data";
import HealthEventData from "../data/health-event.data";
import SafetyEventsData from "../data/other-safety-events.data";
import EpidemicData from "../data/epidemic.data";
import FoodSafetyData from "../data/food-safety.data";
import { TFunction } from "react-i18next";
const apiEndpoint = process.env.REACT_APP_API_URL + "/reportings";

interface ExcelEport {
  perimeter?: string;
  institutionId?: string;
  event?: string;
  status?: string;
  startDate?: string;
  endDate?: string;
}

http.setJwt(getMonitoringJwt()!);

export const getExcelData = async ({
  perimeter,
  institutionId,
  event,
  status,
  startDate,
  endDate,
}: ExcelEport) => {
  http.setJwt(getMonitoringJwt()!);
  const params = {
    perimeter,
    institutionId,
    event,
    status,
    startDate,
    endDate,
  };
  const query = stringify(params, { skipEmptyString: true });
  const { data } = await http.get(`${apiEndpoint}/export?${query}`);

  return data;
};

export const getRelatedEventFields = (event: string) => {
  let fields: any[] = [];
  switch (event) {
    case "Incidents": {
      fields = IncidentData.fields;
      break;
    }
    case "Health event": {
      fields = HealthEventData.fields;
      break;
    }
    case "Other safety events": {
      fields = SafetyEventsData.fields;
      break;
    }
    case "Epidemic": {
      fields = EpidemicData.fields;
      break;
    }
    case "Food Safety Issue": {
      fields = FoodSafetyData.fields;
      break;
    }
    default: {
      fields = [];
      break;
    }
  }
  return fields;
};

export const translateMultipleChioce = (
  value: string,
  t: TFunction<"translation", undefined>
) => {
  const splitedValue = value.split("-,-").map((element, index) => {
    return `${++index}-${t(element)}.`;
  });
  return splitedValue.toString();
};

const formatPeriod = (period: string) => {
  const dateTime = new Date(period);
  const year = dateTime.getFullYear().toString();
  const month = (dateTime.getMonth() + 1).toString().padStart(2, "0");
  const day = dateTime.getDate().toString().padStart(2, "0");
  const formattedDate = `${year}-${month}-${day}`;
  return formattedDate;
};

function convertTo12HourFormat(time24Hour: string): string {
  const [hour, minute] = time24Hour.split(":");
  let hourNum = parseInt(hour);
  let period = "AM";

  if (hourNum === 0) {
    hourNum = 12;
  } else if (hourNum === 12) {
    period = "PM";
  } else if (hourNum > 12) {
    hourNum -= 12;
    period = "PM";
  }

  return `${hourNum < 10 ? `0${hourNum}` : hourNum}:${minute} ${period}`;
}

export const getTranlatedFields = (
  columns: any,
  t: TFunction<"translation", undefined>,
  event: string
) => {
  let fields = getRelatedEventFields(event);
  if (columns.eventTime) {
    columns.eventTime = convertTo12HourFormat(columns.eventTime);
  }
  columns.period = formatPeriod(columns.period);
  const arrangedColumn: any = {};
  const extrafields: any = {};
  fields.forEach((field) => {
    arrangedColumn[field.name] = columns[field.name];
  });

  for (const [key, value] of Object.entries(columns)) {
    if (!arrangedColumn.hasOwnProperty(key) && key !== "__v") {
      extrafields[key] = value;
    }
  }
  const orderedColumns = { ...extrafields, ...arrangedColumn };
  const translatedColumns = Object.fromEntries(
    Object.entries(orderedColumns).map(([key, value]) => {
      const field = fields.find((field) => field.name === key);
      if (field) {
        const valueAsString = value as string;
        let translatedValue;
        if (valueAsString) {
          translatedValue = `${valueAsString}`?.includes("-,-")
            ? translateMultipleChioce(valueAsString, t)
            : t(valueAsString);
        } else {
          translatedValue = t(valueAsString);
        }
        return [
          field.name !== "area" ? t(field.label) : "incident location",
          translatedValue,
        ];
      } else {
        return [t(key), t(value as string)];
      }
    })
  );
  return translatedColumns;
};

export const formatSingleEventData = (
  data: any[],
  t: TFunction<"translation", undefined>,
  event: string
) => {
  return data
    .map((row) => {
      const { details, ...rest } = row;
      const det = details?.length > 0 ? details[0] : details;
      const { __v, event_id, ...excludedData } = rest;
      const columns = { ...excludedData, ...det };
      return getTranlatedFields(columns, t, event);
    })
    .sort((a: any, b: any) => {
      const dateA = new Date(a.date);
      const dateB = new Date(b.date);
      return dateA.getTime() - dateB.getTime();
    });
};
